import { record, upper, type Row } from '../janus-engine/ability/context';
import { placedTrapItem, placedTrapVerdict, TRAPPED_ITEM_STATUS } from '../janus-engine/traps/placed-item';
import { inventoryField } from '../shared/array-types';
import { withAsyncDeadline } from '../shared/async-deadline';
import { heldInventory, inventoryAppliedFrame } from '../shared/inventory-applied-truth';
import { appliedStatusRows } from '../janus-engine/transport/applied-status-rows';
import { withStatusFrameInputs } from '../janus-engine/transport/status-frame-inputs';
import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { abilityTruthReaders, abilityTruthCampaign } from './ability-truth';
import { actorEncounterFor } from './movement-truth';
import { fetchAndCacheObject } from './object-cache';
import { queueActorSave } from './applied-save-outbox';
import { refuseUnported } from '../janus-engine/unported';

const active = new Set<string>();

/** One status verdict mounts and arms, or retires both rows. The existing GM saver owns row authority. */
async function makePlacedItemTrapped(inventoryId: string, abilityId: string | null): Promise<boolean> {
    const id = upper(inventoryId);
    if (active.has(id)) return false;
    active.add(id);
    try {
        return await withAsyncDeadline(async signal => {
            if ((window as Window & { ftActiveViewerIsFullGm?: () => boolean }).ftActiveViewerIsFullGm?.() !== true) throw new Error('Only the GM can change an item’s trap.');
            const eid = upper(window.ftGetActiveEncounterId?.());
            await window.ftWaitForInventoryBase?.(eid);
            const encounter = record(actorEncounterFor(eid)), host = heldInventory(id);
            if (!encounter || !host) throw new Error('trap-item:held-item-unavailable');
            const possessId = upper(inventoryField(host, 'possess_id'));
            if (abilityId && !placedTrapItem(host, eid, parent => heldInventory(parent)))
                throw new Error('Only placed items can be made trapped.');
            if (abilityId) await fetchAndCacheObject(upper(abilityId));
            await fetchAndCacheObject(TRAPPED_ITEM_STATUS);
            signal.throwIfAborted();
            const current = heldInventory(id);
            if (!current || upper(inventoryField(current, 'possess_id')) !== possessId)
                throw new Error('trap-item:holder-changed');
            if (abilityId && !placedTrapItem(current, eid, parent => heldInventory(parent))) throw new Error('Only placed items can be made trapped.');
            const read = abilityTruthReaders(current, encounter, new Set<string>());
            const ability = abilityId ? read.definition(upper(abilityId)) : null;
            if (abilityId && !ability) throw new Error('trap-item:definition-unavailable');
            const verdict = placedTrapVerdict(current, ability, read);
            if (!verdict.statusesAdded.length && !verdict.statusesRemoved.length) return true;
            const command = { schema: 'janus.status.truth.v1', operation_id: crypto.randomUUID().toUpperCase(),
                encounter_id: eid, actor_id: eid, expected_rev: 0, kind: 'status_add', roll_private: 0,
                gm_boundary: verdict, inventory_status_targets: [id] };
            const frame = inventoryAppliedFrame(command);
            frame.rows = appliedStatusRows(verdict.statusesAdded, abilityTruthCampaign(encounter) ?? {});
            frame.deletes = verdict.statusesRemoved.map(status => ({ table: 'statuses', row_id: status.status_id! }));
            withStatusFrameInputs(frame, verdict.statusesAdded, encounter, read.definition);
            if (!window.ftPushAppliedTruth) throw new Error('trap-item:transport-unavailable');
            void Promise.resolve(window.ftPushAppliedTruth({ type: 'janus_status_truth', command, applied_frame: frame }))
                .catch(error => console.warn('[trap-item] local installation failed', error));
            queueActorSave({ encounterId: eid, actorIds: [eid], command,
                send: async () => {
                    const response = await saveAppliedTruth('/options/game/actions/status_truth_commit_inc.php', command, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command), signal: AbortSignal.timeout(10000) });
                    const body = await response.json() as Row;
                    if (response.status >= 500) throw new Error(String(body.error ?? response.status));
                    return { ok: response.ok && body.ok === true, status: response.status, body };
                },
                refused: async () => { displayWarning('The trap change could not be saved; the item’s current state is being restored.');
                    await window.ftResyncEncounter?.(eid); },
            });
            return true;
        }, 20000);
    } catch (error) { refuseUnported(error instanceof Error ? error.message : 'trap-item:unavailable'); return false; }
    finally { active.delete(id); }
}
window.ftMakePlacedItemTrapped = makePlacedItemTrapped;
